import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { TIAdminDashboard, TITrainerDashboard, TIEmployeeDashboard } from '../../components/dashboards/TIDashboard';
import PaidStudentDashboard from '../../components/dashboards/PaidStudentDashboard';

// ── Helpers ──────────────────────────────────────────────────────────────────
function toEmbedUrl(url) {
  if (!url) return '';
  if (url.includes('/embed/')) return url;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : url;
}
const _serverBase = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
function uploadUrl(p) {
  if (!p) return '';
  if (p.startsWith('http')) return p;
  return `${_serverBase}${p}`;
}

// Fetches /uploads/* with the JWT so the auth middleware lets it through.
// Keeps a module-level cache so the same path isn't re-fetched on re-render.

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}
function timeAgo(date) {
  const s = Math.floor((Date.now() - new Date(date)) / 1000);
  if (s < 60)   return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400)return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// ── SVG Charts ───────────────────────────────────────────────────────────────
function BarChart({ data = [], color = '#6366f1', label = '' }) {
  if (!data.length) return <EmptyChart label={label} />;
  const max = Math.max(...data.map(d => d.value), 1);
  const W = 480, H = 140, padX = 48, padY = 28;
  const plotW = W - padX * 2;
  const plotH = H - padY * 2;
  const barW  = plotW / data.length - 6;
  return (
    <div style={{ position: 'relative', width: '100%', paddingBottom: `${(H / W) * 100}%` }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet"
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
        <defs>
          <linearGradient id={`bar${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="1" />
            <stop offset="100%" stopColor={color} stopOpacity="0.55" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75, 1].map(f => (
          <line key={f} x1={padX} x2={W - 12}
            y1={padY + plotH - plotH * f} y2={padY + plotH - plotH * f}
            stroke="#e2e8f0" strokeWidth="1" />
        ))}
        {[0.5, 1].map(f => (
          <text key={f} x={padX - 6} y={padY + plotH - plotH * f + 4}
            textAnchor="end" style={{ fontSize: 9, fill: '#94a3b8' }}>
            {Math.round(max * f)}
          </text>
        ))}
        {data.map((d, i) => {
          const bh = Math.max((d.value / max) * plotH, 2);
          const x  = padX + i * (plotW / data.length) + 3;
          const y  = padY + plotH - bh;
          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={bh} rx="3"
                fill={`url(#bar${color.replace('#','')})`} />
              {d.value > 0 && (
                <text x={x + barW / 2} y={y - 4} textAnchor="middle"
                  style={{ fontSize: 10, fill: color, fontWeight: 700 }}>{d.value}</text>
              )}
              <text x={x + barW / 2} y={H - padY + 14} textAnchor="middle"
                style={{ fontSize: 9, fill: '#94a3b8' }}>{d.label}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function DonutChart({ slices = [], label = '', centerLabel = '' }) {
  if (!slices.length) return <EmptyChart label={label} />;
  const total = slices.reduce((s, d) => s + d.value, 0) || 1;
  const R = 42, r = 26, cx = 70, cy = 70;
  let angle = -Math.PI / 2;
  const paths = slices.map(slice => {
    const sweep = (slice.value / total) * 2 * Math.PI;
    const x1 = cx + R * Math.cos(angle), y1 = cy + R * Math.sin(angle);
    const x2 = cx + R * Math.cos(angle + sweep), y2 = cy + R * Math.sin(angle + sweep);
    const ix1 = cx + r * Math.cos(angle), iy1 = cy + r * Math.sin(angle);
    const ix2 = cx + r * Math.cos(angle + sweep), iy2 = cy + r * Math.sin(angle + sweep);
    const large = sweep > Math.PI ? 1 : 0;
    const d = `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${r} ${r} 0 ${large} 0 ${ix1} ${iy1} Z`;
    angle += sweep;
    return { ...slice, d, pct: Math.round((slice.value / total) * 100) };
  });
  return (
    <div className="d-flex align-items-center gap-3 flex-wrap">
      <div style={{ width: 'min(140px, 40%)', flexShrink: 0 }}>
        <svg viewBox="0 0 140 140" preserveAspectRatio="xMidYMid meet"
          style={{ width: '100%', height: 'auto', display: 'block' }}>
          {paths.map((p, i) => (
            <path key={i} d={p.d} fill={p.color} opacity="0.9" />
          ))}
          <circle cx={cx} cy={cy} r={r - 2} fill="white" />
          {centerLabel && (
            <>
              <text x={cx} y={cy - 3} textAnchor="middle"
                style={{ fontSize: 11, fontWeight: 800, fill: '#1e293b' }}>
                {centerLabel.split('\n')[0]}
              </text>
              <text x={cx} y={cy + 10} textAnchor="middle"
                style={{ fontSize: 6, fill: '#94a3b8' }}>
                {centerLabel.split('\n')[1] || ''}
              </text>
            </>
          )}
        </svg>
      </div>
      <div className="d-flex flex-column gap-1 flex-grow-1">
        {paths.map((p, i) => (
          <div key={i} className="d-flex align-items-center gap-2">
            <div style={{ width: 10, height: 10, borderRadius: 2, background: p.color, flexShrink: 0 }}></div>
            <div style={{ fontSize: 12, color: '#334155', flexGrow: 1 }}>{p.label}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: p.color }}>{p.pct}%</div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>({p.value})</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HorizontalBar({ label, value, max, color, suffix = '' }) {
  const pct = max ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="mb-2">
      <div className="d-flex justify-content-between mb-1">
        <span style={{ fontSize: 12, color: '#334155', fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color }}>{value}{suffix}</span>
      </div>
      <div style={{ height: 7, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width 0.8s ease' }}></div>
      </div>
    </div>
  );
}

function EmptyChart({ label = '' }) {
  return (
    <div className="text-center text-muted py-3">
      <i className="bi bi-bar-chart-line" style={{ fontSize: 28, opacity: 0.2 }}></i>
      <div style={{ fontSize: 12, marginTop: 6 }}>No data yet{label ? ` for ${label}` : ''}</div>
    </div>
  );
}

function KpiCard({ icon, label, value, sub, color, bg, trend, onClick }) {
  return (
    <div className="card h-100 shadow-sm" style={{ borderRadius: 14, cursor: onClick ? 'pointer' : 'default', border: `1px solid ${bg}` }}
      onClick={onClick}>
      <div className="card-body" style={{ padding: '16px 18px' }}>
        <div className="d-flex align-items-start justify-content-between">
          <div className="rounded-3 d-flex align-items-center justify-content-center"
            style={{ width: 44, height: 44, background: bg, flexShrink: 0 }}>
            <i className={`bi ${icon}`} style={{ fontSize: 20, color }}></i>
          </div>
          {trend !== undefined && (
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
              background: trend >= 0 ? '#d1fae5' : '#fee2e2',
              color: trend >= 0 ? '#166534' : '#991b1b',
            }}>
              {trend >= 0 ? '▲' : '▼'} {Math.abs(trend)}%
            </span>
          )}
        </div>
        <div style={{ marginTop: 12, fontSize: 26, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
          {value ?? <span style={{ fontSize: 16, color: '#94a3b8' }}>—</span>}
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, fontWeight: 500 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function TenantDashboard() {
  const { user, tenant, role: roleDoc } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats,   setStats]   = useState(null);
  const [myAssessments,  setMyAssessments]  = useState([]);
  const [certRecipients, setCertRecipients] = useState([]);
  const [promotions,     setPromotion]      = useState([]);
  const [certIdx,        setCertIdx]        = useState(0);
  const [slideIdx,       setSlideIdx]       = useState(0);
  const [certShowIdx,    setCertShowIdx]    = useState(0);
  const [videoModal,     setVideoModal]     = useState(null);
  const [certModal,      setCertModal]      = useState(null);
  // Today's completed topics for the student's enrolled batches. Populated
  // by the /training/course-records/daily-records/summary endpoint which
  // auto-filters to the student's batches. The widget only renders when
  // this array has at least one record.
  const [todayTopics,    setTodayTopics]    = useState([]);
  // Bumped by the Header refresh button. Children use this as a
  // useEffect dep so they reload when the user clicks Refresh,
  // without the child calling the parent's setState in return
  // (which would create an infinite re-mount loop).
  const [refreshKey, setRefreshKey] = useState(0);

  const role = user?.roleKey || user?.role || '';
  const isAdminLike   = ['super_admin', 'admin'].includes(role);
  const isManager     = role === 'manager';
  const isTeamLead    = role === 'team_lead';
  const isFaculty     = ['faculty', 'teacher', 'trainer'].includes(role);
  const isStudent     = role === 'student';
  const isParent      = role === 'parent';
  const isStaff       = role === 'staff';
  const isViewer      = role === 'viewer';
  const isAdminOrAbove = isAdminLike || isManager || isTeamLead;

  // Role theme — each role gets a distinct color identity so the dashboard
  // visibly changes shape between roles, not just data values.
  const ROLE_THEME = {
    super_admin: { color: '#dc2626', bg: '#fee2e2', label: 'Super Admin',   tagline: 'Full platform overview' },
    admin:       { color: '#6366f1', bg: '#ede9fe', label: 'Administrator', tagline: 'Organization overview' },
    manager:     { color: '#0ea5e9', bg: '#e0f2fe', label: 'Manager',       tagline: 'Operations overview' },
    team_lead:   { color: '#8b5cf6', bg: '#f3e8ff', label: 'Team Lead',     tagline: 'Your team at a glance' },
    faculty:     { color: '#14b8a6', bg: '#ccfbf1', label: 'Faculty',       tagline: 'Your classes & students' },
    teacher:     { color: '#14b8a6', bg: '#ccfbf1', label: 'Teacher',       tagline: 'Your classes & students' },
    student:     { color: '#22c55e', bg: '#d1fae5', label: 'Student',       tagline: 'Your learning journey' },
    parent:      { color: '#ec4899', bg: '#fce7f3', label: 'Parent',        tagline: 'Your children at a glance' },
    staff:       { color: '#f59e0b', bg: '#fef3c7', label: 'Staff',         tagline: 'Your tasks & schedule' },
    viewer:      { color: '#64748b', bg: '#f1f5f9', label: 'Viewer',        tagline: 'Read-only access' },
  };
  const theme = ROLE_THEME[role] || ROLE_THEME.viewer;

  // Industry detection — drives whether KPIs read "Batches" (training tenant)
  // or "Classes" (education tenant). `tenant.industryId` may be populated
  // (object with .slug) or just an ObjectId string, depending on how the
  // tenant was loaded; coerce defensively.
  const industrySlug = (() => {
    const t = tenant;
    if (!t) return '';
    const refs = [t.industryId, t.industry, t.industrySlug];
    for (const ref of refs) {
      if (typeof ref === 'string' && ref) return ref.toLowerCase();
      if (ref && typeof ref === 'object') {
        if (typeof ref.slug === 'string') return ref.slug.toLowerCase();
        if (typeof ref.name === 'string') return ref.name.toLowerCase();
      }
    }
    return '';
  })();
  const isEducationTenant = industrySlug === 'education' || industrySlug === 'school';

  const dayName = new Date().toLocaleDateString('en-US', { weekday: 'long' });
  const dateStr = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/dashboard').catch(() => ({ data: { data: {} } }));
      setStats(r.data?.data || {});

      if (isStudent) {
        const [a, c, p, t] = await Promise.allSettled([
          api.get('/training/assessments/my'),
          api.get('/training/certificates/recent-issued', { params: { limit: 12 } }),
          api.get('/training/promotions'),
          // Today's completed topics — backend filters to the student's
          // enrolled batches, returns up to 14 days. We pick today's
          // entry (recordDate starts at the start of UTC) for the
          // "Today's Completed Topics" widget.
          api.get('/training/course-records/daily-records/summary', { params: { days: 2 } }),
        ]);
        if (a.status === 'fulfilled') setMyAssessments(a.value.data?.records || a.value.data?.data || []);
        if (c.status === 'fulfilled') setCertRecipients(c.value.data?.records || c.value.data?.data || []);
        if (p.status === 'fulfilled') setPromotion(p.value.data?.records || p.value.data?.data || []);
        if (t.status === 'fulfilled') {
          const records = t.value.data?.records || [];
          // Filter to today's recordDate (UTC date comparison).
          const today = new Date();
          const todayKey = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
          const todaysRecords = records.filter(r => {
            const d = new Date(r.recordDate);
            const k = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
            return k === todayKey;
          });
          setTodayTopics(todaysRecords);
        }
      }
    } finally { setLoading(false); }
  }, [isStudent]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-refresh — keep the dashboard graphs and KPIs "live"
  // without forcing the user to hit Refresh. We poll every 60s
  // for student dashboards (so attendance / progress / payments
  // tick over without a page reload) and every 5 minutes for
  // management dashboards (cheaper, less chatty). Polling
  // pauses while the tab is hidden and resumes on focus so we
  // don't drain the user's battery or hit the API pointlessly.
  useEffect(() => {
    const interval = isStudent ? 60 * 1000 : 5 * 60 * 1000;
    let timer = null;
    const tick = () => loadAll();
    const start = () => {
      if (timer) return;
      timer = setInterval(tick, interval);
    };
    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };
    const onVisibility = () => (document.hidden ? stop() : (tick(), start()));
    document.addEventListener('visibilitychange', onVisibility);
    start();
    return () => { stop(); document.removeEventListener('visibilitychange', onVisibility); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStudent, loadAll]);

  // Refresh handler used by the Header's Refresh button. Bumps
  // `refreshKey` so any TI dashboard child re-runs its data fetch,
  // and re-runs the parent's own loadAll to refresh stats / carousel
  // data. Wrapped in useCallback so the Header's onClick reference
  // stays stable across renders (avoids unnecessary re-renders).
  const handleRefresh = useCallback(() => {
    setRefreshKey(k => k + 1);
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (certRecipients.length < 2) return;
    const t = setInterval(() => setCertIdx(i => (i + 1) % certRecipients.length), 4000);
    return () => clearInterval(t);
  }, [certRecipients.length]);

  useEffect(() => {
    const slides = promotions.filter(p => p.type === 'carousel_slide');
    const pairCount = Math.ceil(slides.length / 2);
    if (pairCount < 2) return;
    const t = setInterval(() => setSlideIdx(i => (i + 1) % pairCount), 5000);
    return () => clearInterval(t);
  }, [promotions]);

  useEffect(() => {
    const count = promotions.filter(p => p.type === 'certificate_showcase').length;
    if (count < 2) return;
    const t = setInterval(() => setCertShowIdx(i => (i + 1) % count), 3500);
    return () => clearInterval(t);
  }, [promotions]);

  // Only show the full-page spinner on the *initial* load (when we
  // don't have any stats yet). On subsequent refreshes we keep the
  // page mounted so child components don't unmount/remount and
  // cause a visible flicker. The Header's Refresh button already
  // shows its own spinner state via the api response.
  if (loading && !stats) return <LoadingSpinner fullPage />;

  // ── Header (shared, themed per role) ──────────────────────────────────────
  const Header = (
    <div
      className="mb-4 p-3 rounded-3 d-flex align-items-center justify-content-between flex-wrap gap-2"
      style={{
        background: `linear-gradient(135deg, ${theme.bg} 0%, #ffffff 100%)`,
        borderLeft: `4px solid ${theme.color}`,
      }}
    >
      <div className="d-flex align-items-center gap-3">
        <div
          className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: 50, height: 50, background: theme.color, color: '#fff', fontWeight: 800, fontSize: 20 }}
        >
          {(user?.firstName?.[0] || '?').toUpperCase()}
        </div>
        <div>
          <h4 style={{ fontWeight: 800, fontSize: 20, color: '#1e293b', marginBottom: 2 }}>
            {greeting()}, {user?.firstName || 'there'} 👋
          </h4>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            <i className="bi bi-calendar3 me-1"></i>
            {dayName}, {dateStr}
            {tenant?.name && <> · <strong>{tenant.name}</strong></>}
          </div>
          <div style={{ marginTop: 4 }}>
            <span className="badge" style={{ background: theme.color, color: '#fff', fontSize: 10, fontWeight: 700, padding: '4px 9px' }}>
              {theme.label.toUpperCase()}
            </span>
            <span style={{ fontSize: 11, color: '#64748b', marginLeft: 8 }}>{theme.tagline}</span>
          </div>
        </div>
      </div>
      <button className="btn btn-sm" style={{ background: theme.color, color: '#fff' }} onClick={handleRefresh}>
        <i className="bi bi-arrow-clockwise me-1"></i>Refresh
      </button>
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════════
  //  TRAINING INSTITUTE — Enhanced analytics dashboard
  //  Renders ONLY for tenants whose industry slug is `training_institute`.
  //  Every other tenant (education, software, retail, …) keeps the existing
  //  role-based dashboard structure below untouched.
  // ════════════════════════════════════════════════════════════════════════════
  if (industrySlug === 'training_institute') {
    // Use the tenant's branded primary colour so all chart accents
    // (lines, bars, donuts, hover states) feel native to the theme.
    // Fall back to the role theme colour when the tenant hasn't set one.
    const brandColor = tenant?.branding?.primaryColor || theme.color;
    return (
      <div style={{ paddingBottom: 32 }}>
        {!isStudent && Header}
        {(() => {
          if (isStudent) return <PaidStudentDashboard refreshKey={refreshKey} />;
          if (isFaculty) return <TITrainerDashboard refreshKey={refreshKey} themeColor={brandColor} />;
          if (isAdminOrAbove) return <TIAdminDashboard role={role} refreshKey={refreshKey} themeColor={brandColor} />;
          // Non-student non-trainer non-admin — treat as employee analytics
          return <TIEmployeeDashboard refreshKey={refreshKey} themeColor={brandColor} />;
        })()}
      </div>
    );
  }

  // Quick Access removed: it bypassed the role-based access matrix by linking
  // straight to module routes regardless of whether the current user's role
  // could view them. Role-aware navigation now lives entirely in the sidebar.

  // ════════════════════════════════════════════════════════════════════════════
  //  STUDENT DASHBOARD
  // ════════════════════════════════════════════════════════════════════════════
  if (isStudent) {
    const ss = stats?.studentStats || {};
    const att = ss.myAttendance || { pct: null, present: 0, total: 0 };
    const fees = ss.myFees || { totalDue: 0, totalPaid: 0, balance: 0, nextDue: null };
    const carouselSlides = promotions.filter(p => p.type === 'carousel_slide');
    const videoReviews   = promotions.filter(p => p.type === 'video_review');
    const promoItems     = promotions.filter(p => p.type === 'promotion');
    const certShowcase   = promotions.filter(p => p.type === 'certificate_showcase');
    // Group slides into pairs of 2
    const carouselPairs = [];
    for (let i = 0; i < carouselSlides.length; i += 2) carouselPairs.push(carouselSlides.slice(i, i + 2));
    const pairActive = carouselPairs.length > 0 ? slideIdx % carouselPairs.length : 0;

    return (
      <div style={{ paddingBottom: 32 }}>

        {/* ── Hero Carousel — single image card per slide, auto-advances ──── */}
        {carouselPairs.length > 0 && (
          <div className="mb-4" style={{ marginTop: -16 }}>
            <div id="heroCarousel" className="carousel slide" data-bs-ride="carousel" data-bs-interval="4000">
              <div className="carousel-inner">
                {carouselPairs.map((pair, pi) => (
                  <div key={pi} className={`carousel-item ${pi === pairActive ? 'active' : ''}`}>
                    <div style={{ display: 'flex', gap: 12, padding: '0 4px' }}>
                      {pair.map((slide, si) => {
                        const imgSrc = slide.imageUrl ? uploadUrl(slide.imageUrl) : '';
                        return (
                          <div
                            key={slide._id}
                            style={{
                              flex: 1,
                              borderRadius: 16,
                              overflow: 'hidden',
                              height: 220,
                              position: 'relative',
                              background: slide.bgColor || '#1e293b',
                              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            }}
                          >
                            {imgSrc ? (
                              <img
                                src={imgSrc}
                                alt={slide.title || `Slide ${pi * 2 + si + 1}`}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                              />
                            ) : (
                              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="bi bi-image" style={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                              </div>
                            )}
                            {slide.title && (
                              <div style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                padding: '20px 16px 16px',
                                background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                                color: 'white',
                              }}>
                                <div style={{ fontSize: 14, fontWeight: 700 }}>{slide.title}</div>
                                {slide.subtitle && <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>{slide.subtitle}</div>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              {carouselPairs.length > 1 && (
                <>
                  <button className="carousel-control-prev" type="button" onClick={() => setSlideIdx(i => (i - 1 + carouselPairs.length) % carouselPairs.length)} style={{ width: 40, opacity: 0.6 }}>
                    <span className="carousel-control-prev-icon" />
                  </button>
                  <button className="carousel-control-next" type="button" onClick={() => setSlideIdx(i => (i + 1) % carouselPairs.length)} style={{ width: 40, opacity: 0.6 }}>
                    <span className="carousel-control-next-icon" />
                  </button>
                  <div className="d-flex justify-content-center gap-2 mt-2">
                    {carouselPairs.map((_, pi) => (
                      <div
                        key={pi}
                        onClick={() => setSlideIdx(pi)}
                        style={{
                          width: pi === pairActive ? 24 : 8,
                          height: 8,
                          borderRadius: 4,
                          background: pi === pairActive ? '#6366f1' : '#cbd5e1',
                          cursor: 'pointer',
                          transition: 'all 0.3s',
                        }}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Student Video Reviews — autoplay muted, click for sound ──── */}
        {videoReviews.length > 0 && (
          <div className="mb-4">
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', marginBottom: 12 }}>
              <i className="bi bi-play-circle-fill me-2 text-danger" />What Our Students Say
            </div>
            <div style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 6 }}>
              {videoReviews.map(v => {
                const embedId = v.videoUrl?.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/)?.[1];
                const embedSrc = embedId
                  ? `https://www.youtube.com/embed/${embedId}?autoplay=1&mute=1&loop=1&playlist=${embedId}&controls=1&rel=0`
                  : toEmbedUrl(v.videoUrl);
                return (
                  <div key={v._id} style={{ flexShrink: 0, width: 280, borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0', background: 'white' }}>
                    <div style={{ position: 'relative', paddingBottom: '56.25%', background: '#0f172a' }}>
                      {embedSrc && (
                        <iframe
                          src={embedSrc}
                          title={v.reviewerName}
                          allow="autoplay; encrypted-media; picture-in-picture"
                          allowFullScreen
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
                        />
                      )}
                    </div>
                    <div style={{ padding: '8px 12px 10px' }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{v.reviewerName}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{v.reviewerCourse}{v.reviewerBatch ? ` · ${v.reviewerBatch}` : ''}</div>
                      {(v.rating || 0) > 0 && <div style={{ fontSize: 12, color: '#f59e0b', marginTop: 2 }}>{'★'.repeat(v.rating)}{'☆'.repeat(5 - v.rating)}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {Header}

        {/* KPIs */}
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3">
            <KpiCard icon="bi-journal-bookmark-fill" label="My Courses" value={ss.myEnrollmentCount ?? 0} color="#6366f1" bg="#ede9fe" sub="enrolled & active" />
          </div>
          <div className="col-6 col-md-3">
            <KpiCard
              icon="bi-calendar-check-fill"
              label="My Attendance"
              value={att.pct !== null ? `${att.pct}%` : '—'}
              color={att.pct >= 75 ? '#22c55e' : att.pct >= 50 ? '#f59e0b' : '#ef4444'}
              bg={att.pct >= 75 ? '#d1fae5' : att.pct >= 50 ? '#fef3c7' : '#fee2e2'}
              sub={att.total ? `${att.present}/${att.total} classes` : 'No data yet'}
            />
          </div>
          <div className="col-6 col-md-3">
            <KpiCard icon="bi-clipboard-check" label="My Assessments" value={myAssessments.length} color="#0ea5e9" bg="#e0f2fe" sub="active for me" />
          </div>
          <div className="col-6 col-md-3">
            <KpiCard
              icon="bi-currency-rupee"
              label="Fees Balance"
              value={fees.balance ? `₹${(fees.balance / 1000).toFixed(1)}K` : '✓'}
              color={fees.balance > 0 ? '#ef4444' : '#22c55e'}
              bg={fees.balance > 0 ? '#fee2e2' : '#d1fae5'}
              sub={fees.balance > 0 ? `of ₹${(fees.totalDue / 1000).toFixed(0)}K total` : 'fully paid'}
            />
          </div>
        </div>

        {/* My Assessments */}
        {myAssessments.length > 0 && (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h6 className="mb-0 fw-bold">
                    <i className="bi bi-clipboard-check me-2 text-primary" />My Assessments
                  </h6>
                  <small className="text-muted">Assessments assigned to your batch</small>
                </div>
                <a href="/assessments" className="btn btn-sm btn-outline-primary">
                  View all <i className="bi bi-arrow-right ms-1" />
                </a>
              </div>
              <div className="row g-2">
                {myAssessments.slice(0, 6).map(a => {
                  const sub = a.mySubmission;
                  const overdue = a.endDate && new Date(a.endDate) < new Date() && !sub;
                  const statusColor =
                    sub?.status === 'evaluated' || sub?.status === 'passed' ? 'success' :
                    sub?.status === 'submitted' ? 'info' :
                    overdue ? 'danger' : 'warning';
                  const statusLabel =
                    sub?.status === 'evaluated' || sub?.status === 'passed' ?
                      `${sub.marksObtained ?? 0}/${a.totalMarks} (${sub.percentage ?? 0}%)` :
                    sub?.status === 'submitted' ? 'Submitted — pending review' :
                    overdue ? 'Overdue' : 'Pending';
                  return (
                    <div key={a._id} className="col-md-6 col-lg-4">
                      <div className={`border rounded p-2 h-100 ${overdue && !sub ? 'border-danger' : ''}`} style={{ background: '#fafbfc' }}>
                        <div className="d-flex justify-content-between align-items-start gap-2">
                          <div style={{ flex: 1 }}>
                            <div className="fw-semibold small">{a.title}</div>
                            <div className="small text-muted">
                              {a.course?.courseName || ''}
                              {a.batch?.batchNumber ? ` · ${a.batch.batchNumber}` : ''}
                            </div>
                            {a.endDate && (
                              <div className="small text-muted">
                                <i className="bi bi-clock me-1" />
                                Due {new Date(a.endDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                              </div>
                            )}
                          </div>
                          <span className={`badge bg-${statusColor} small`}>{statusLabel}</span>
                        </div>
                        {!sub && a.status === 'active' && (
                          <a href="/assessments" className="btn btn-sm btn-primary w-100 mt-2">
                            <i className="bi bi-play-fill me-1" />Take Assessment
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Today's Classes + My Enrollments */}
        <div className="row g-3 mb-4">
          <div className="col-lg-6">
            <div className="card h-100 shadow-sm" style={{ borderRadius: 14 }}>
              <div className="card-body">
                <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', marginBottom: 14 }}>
                  <i className="bi bi-calendar3 me-2" style={{ color: '#0ea5e9' }}></i>
                  Today's Classes
                </div>
                {(ss.todayClasses || []).length === 0 ? (
                  <div className="text-center text-muted py-3">
                    <i className="bi bi-cup-hot" style={{ fontSize: 28, opacity: 0.2 }}></i>
                    <div style={{ fontSize: 13, marginTop: 8 }}>No classes scheduled for today</div>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {ss.todayClasses.map((c, i) => (
                      <div key={i} className="d-flex align-items-center gap-2 p-2 rounded" style={{ background: '#f8fafc', borderLeft: '3px solid #0ea5e9' }}>
                        <div className="flex-grow-1">
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{c.courseName}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>
                            {c.batchNumber}{c.facultyName ? ` · ${c.facultyName}` : ''}{c.classroom ? ` · ${c.classroom}` : ''}
                          </div>
                        </div>
                        <div className="text-end">
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#0ea5e9' }}>{c.startTime || '—'}</div>
                          <div style={{ fontSize: 10, color: '#94a3b8' }}>{c.endTime || ''}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card h-100 shadow-sm" style={{ borderRadius: 14 }}>
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                    <i className="bi bi-mortarboard me-2" style={{ color: '#22c55e' }}></i>
                    My Enrollments
                  </div>
                  <a href="/student-course" style={{ fontSize: 12, color: '#6366f1' }}>All courses →</a>
                </div>
                {(ss.myEnrollments || []).length === 0 ? (
                  <div className="text-center text-muted py-3">No enrollments yet</div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {ss.myEnrollments.map(e => {
                      // Per-enrollment progress: { done, total, percent }.
                      // Falls back to 0/0/0 when the course has no
                      // content yet (or the analytics models are
                      // unavailable in this tenant).
                      const prog = e.progress || { done: 0, total: 0, percent: 0 };
                      const pct  = prog.percent || 0;
                      // Click destination — students don't have
                      // access to the management-side /enrollments
                      // route, so we send them to the student-friendly
                      // course-content view scoped by this enrollment.
                      const href = e.courseId
                        ? `/student-course?course=${encodeURIComponent(e.courseId)}&enrollment=${encodeURIComponent(e._id)}`
                        : '/student-course';
                      return (
                        <a key={e._id} href={href} className="d-block p-2 rounded text-decoration-none"
                           style={{ background: '#f8fafc', color: 'inherit', transition: 'background .15s ease' }}
                           onMouseEnter={e2 => e2.currentTarget.style.background = '#eef2ff'}
                           onMouseLeave={e2 => e2.currentTarget.style.background = '#f8fafc'}>
                          <div className="d-flex align-items-center justify-content-between">
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{e.courseName}</div>
                              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                                {e.batchNumber || '—'} · #{e.enrollmentNumber}
                              </div>
                            </div>
                            <div className="d-flex gap-1 align-items-center flex-shrink-0 ms-2">
                              <span className={`badge bg-${e.status === 'active' || e.status === 'ongoing' ? 'success' : 'secondary'}`} style={{ fontSize: 10 }}>{e.status}</span>
                              <span className={`badge bg-${e.paymentStatus === 'completed' ? 'success' : e.paymentStatus === 'overdue' ? 'danger' : 'warning'}`} style={{ fontSize: 10 }}>
                                {e.paymentStatus}
                              </span>
                              <i className="bi bi-chevron-right" style={{ color: '#94a3b8', fontSize: 12 }}></i>
                            </div>
                          </div>
                          {/* Progress bar — driven by real backend
                              progress (faculty-completed topics / total
                              content items), not a phantom 0%. */}
                          <div className="d-flex align-items-center gap-2 mt-2">
                            <div className="progress flex-grow-1" style={{ height: 5 }}>
                              <div
                                className="progress-bar"
                                role="progressbar"
                                style={{
                                  width: `${pct}%`,
                                  background: pct >= 100
                                    ? 'linear-gradient(90deg, #22c55e, #16a34a)'
                                    : 'linear-gradient(90deg, #3b82f6, #6366f1)',
                                }}
                                aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}
                              />
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 700, color: pct >= 100 ? '#16a34a' : '#3b82f6', minWidth: 32, textAlign: 'right' }}>
                              {pct}%
                            </span>
                          </div>
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Today's Completed Topics — read from /training/course-records
            /daily-records/summary. Shows what faculty covered in the
            student's enrolled batches today (and the last few days). */}
        {todayTopics.length > 0 && (
          <div className="row g-3 mb-4">
            <div className="col-12">
              <div className="card shadow-sm" style={{ borderRadius: 14 }}>
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                      <i className="bi bi-journal-check me-2" style={{ color: '#22c55e' }}></i>
                      Today's Completed Topics
                    </div>
                    <span className="badge bg-light text-dark border small">
                      {todayTopics[0]?.courseId?.courseName || ''}
                      {todayTopics[0]?.batchId?.batchNumber ? ` · ${todayTopics[0].batchId.batchNumber}` : ''}
                    </span>
                  </div>
                  <div className="row g-2">
                    {todayTopics[0].completedTopics.map((t, i) => (
                      <div key={t.topicId || i} className="col-md-6 col-lg-4">
                        <div className="d-flex align-items-start gap-2 p-2 rounded" style={{ background: '#f0fdf4', borderLeft: '3px solid #22c55e' }}>
                          <i className="bi bi-check-circle-fill mt-1" style={{ color: '#22c55e', fontSize: 14 }}></i>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{t.topicName}</div>
                            {t.moduleName && <div style={{ fontSize: 11, color: '#64748b' }}>{t.moduleName}</div>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {todayTopics[0]?.facultyId && (
                    <div className="text-muted small mt-2">
                      <i className="bi bi-person me-1"></i>
                      Faculty: {todayTopics[0].facultyId.firstName} {todayTopics[0].facultyId.lastName}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Fees Summary (if balance > 0) */}
        {fees.balance > 0 && (
          <div className="card shadow-sm mb-4" style={{ borderRadius: 14, background: 'linear-gradient(135deg, #fee2e2 0%, #fef3c7 100%)' }}>
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#991b1b' }}>
                    <i className="bi bi-currency-rupee me-2"></i>Pending Fees
                  </div>
                  <div style={{ fontSize: 12, color: '#7c2d12', marginTop: 4 }}>
                    Outstanding ₹{fees.balance.toLocaleString('en-IN')} of total ₹{fees.totalDue.toLocaleString('en-IN')}.
                    Paid ₹{fees.totalPaid.toLocaleString('en-IN')} so far.
                    {fees.nextDue && (
                      <> Next due ₹{fees.nextDue.amount?.toLocaleString('en-IN')} on {new Date(fees.nextDue.date).toLocaleDateString('en-IN')}.</>
                    )}
                  </div>
                </div>
                {/* Students don't have access to /enrollments (the
                    management view) — send them to the student-friendly
                    course view where they can pay installments. Management
                    roles go to /enrollments as before. */}
                <a href={isStudent ? '/student-course' : '/enrollments'} className="btn btn-danger btn-sm">View Details →</a>
              </div>
            </div>
          </div>
        )}

        {/* ── Promotions ──────────────────────────────────────────────────── */}
        {promoItems.map(promo => {
          const imgSrc = uploadUrl(promo.imageUrl);
          return (
            <div key={promo._id} className="mb-4" style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              {imgSrc && <img src={imgSrc} alt="" style={{ width: '100%', maxHeight: 220, objectFit: 'cover', display: 'block' }} />}
              {(promo.promoTitle || promo.promoText) && (
                <div style={{ padding: '16px 20px', background: '#f8fafc' }}>
                  {promo.promoTitle && <div style={{ fontWeight: 700, fontSize: 17, color: '#1e293b' }}>{promo.promoTitle}</div>}
                  {promo.promoText && <div style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>{promo.promoText}</div>}
                  <div className="d-flex gap-2 flex-wrap mt-3">
                    {promo.ctaText && promo.ctaLink && (
                      <a href={promo.ctaLink} className="btn btn-primary btn-sm" target="_blank" rel="noopener noreferrer">{promo.ctaText}</a>
                    )}
                    {promo.promoVideoUrl && (
                      <button className="btn btn-outline-danger btn-sm" onClick={() => setVideoModal({ videoUrl: promo.promoVideoUrl, reviewerName: promo.promoTitle || 'Promo Video' })}>
                        <i className="bi bi-play-fill me-1" />Watch Video
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* ── Certificate Showcase (admin-posted soft copies) ─────────────── */}
        {certShowcase.length > 0 && (
          <div className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                <i className="bi bi-award-fill me-2" style={{ color: '#6366f1' }} />Student Certificates
              </div>
              {certShowcase.length > 1 && (
                <div className="d-flex gap-1">
                  <button className="btn btn-sm btn-outline-secondary py-0 px-2" onClick={() => setCertShowIdx(i => (i - 1 + certShowcase.length) % certShowcase.length)}><i className="bi bi-chevron-left" /></button>
                  <button className="btn btn-sm btn-outline-secondary py-0 px-2" onClick={() => setCertShowIdx(i => (i + 1) % certShowcase.length)}><i className="bi bi-chevron-right" /></button>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 8 }}>
              {certShowcase.map((cert, k) => {
                const imgSrc = cert.certificateImageUrl ? uploadUrl(cert.certificateImageUrl) : '';
                return (
                  <div
                    key={cert._id}
                    onClick={() => setCertModal(cert)}
                    style={{
                      flexShrink: 0,
                      width: 220,
                      borderRadius: 14,
                      overflow: 'hidden',
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    }}
                  >
                    {/* Certificate Image Area */}
                    <div style={{
                      height: 160,
                      background: imgSrc ? '#f8fafc' : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a78bfa 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 12,
                      position: 'relative',
                    }}>
                      {imgSrc ? (
                        <img
                          src={imgSrc}
                          alt={`${cert.studentName} certificate`}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            borderRadius: 6,
                            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                          }}
                        />
                      ) : (
                        <div style={{ textAlign: 'center' }}>
                          <i className="bi bi-award-fill" style={{ fontSize: 48, color: 'rgba(255,255,255,0.8)' }} />
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>Certificate</div>
                        </div>
                      )}
                      {/* Overlay indicator */}
                      <div style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        background: 'rgba(99,102,241,0.9)',
                        borderRadius: 20,
                        padding: '4px 8px',
                        fontSize: 10,
                        color: 'white',
                      }}>
                        <i className="bi bi-arrows-fullscreen me-1" style={{ fontSize: 9 }} />
                        View
                      </div>
                    </div>
                    {/* Certificate Info */}
                    <div style={{ padding: '12px 14px 14px' }}>
                      <div style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#1e293b',
                        marginBottom: 2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>{cert.studentName || 'Student'}</div>
                      <div style={{
                        fontSize: 11,
                        color: '#64748b',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        marginBottom: 4,
                      }}>{cert.courseName || cert.batchName || ''}</div>
                      {cert.achievement && (
                        <span style={{
                          display: 'inline-block',
                          background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                          color: 'white',
                          fontSize: 9,
                          fontWeight: 700,
                          padding: '3px 8px',
                          borderRadius: 20,
                          marginBottom: 4,
                        }}>{cert.achievement}</span>
                      )}
                      {cert.issuedDate && (
                        <div style={{ fontSize: 10, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <i className="bi bi-calendar3" />
                          {new Date(cert.issuedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Certificate Lightbox ─────────────────────────────────────────── */}
        {certModal && (
          <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.75)' }} onClick={() => setCertModal(null)}>
            <div className="modal-dialog modal-lg modal-dialog-centered" onClick={e => e.stopPropagation()}>
              <div className="modal-content border-0">
                <div className="modal-header border-0 pb-0">
                  <div>
                    <div className="fw-bold" style={{ fontSize: 16 }}>{certModal.studentName}</div>
                    <small className="text-muted">{certModal.courseName}{certModal.batchName ? ` · ${certModal.batchName}` : ''}</small>
                  </div>
                  <button className="btn-close" onClick={() => setCertModal(null)} />
                </div>
                <div className="modal-body p-3">
                  {certModal.certificateImageUrl
                    ? <img src={uploadUrl(certModal.certificateImageUrl)} alt="Certificate" style={{ width: '100%', borderRadius: 8, display: 'block' }} />
                    : <div className="text-center py-5 text-muted">No certificate image available</div>
                  }
                </div>
                {(certModal.achievement || certModal.issuedDate) && (
                  <div className="modal-footer border-0 pt-0 justify-content-start gap-2">
                    {certModal.achievement && <span className="badge bg-warning text-dark">{certModal.achievement}</span>}
                    {certModal.issuedDate && <small className="text-muted"><i className="bi bi-calendar3 me-1" />{new Date(certModal.issuedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</small>}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Video Lightbox ───────────────────────────────────────────────── */}
        {videoModal && (
          <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.72)' }} onClick={() => setVideoModal(null)}>
            <div className="modal-dialog modal-lg modal-dialog-centered" onClick={e => e.stopPropagation()}>
              <div className="modal-content border-0">
                <div className="modal-header border-0">
                  <div>
                    <h5 className="modal-title mb-0">{videoModal.reviewerName}</h5>
                    {videoModal.reviewerCourse && <small className="text-muted">{videoModal.reviewerCourse}</small>}
                  </div>
                  <button className="btn-close" onClick={() => setVideoModal(null)} />
                </div>
                <div className="modal-body p-0">
                  <div style={{ position: 'relative', paddingBottom: '56.25%' }}>
                    <iframe src={toEmbedUrl(videoModal.videoUrl)} title={videoModal.reviewerName} allowFullScreen
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} />
                  </div>
                  {videoModal.reviewText && (
                    <div className="p-3 text-muted fst-italic" style={{ fontSize: 14 }}>"{videoModal.reviewText}"</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  //  PARENT DASHBOARD (Education tenant) — shows one card per child
  // ════════════════════════════════════════════════════════════════════════════
  if (isParent) {
    const ps = stats?.parentStats || {};
    const children = ps.children || [];

    return (
      <div style={{ paddingBottom: 32 }}>
        {Header}

        {/* Welcome / summary */}
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <KpiCard
              icon="bi-people-fill"
              label="My Children"
              value={ps.childrenCount ?? 0}
              color="#6366f1" bg="#ede9fe"
              sub={ps.childrenCount === 1 ? 'enrolled at this school' : 'enrolled at this school'}
            />
          </div>
          <div className="col-md-6">
            <KpiCard
              icon="bi-info-circle-fill"
              label="Quick links"
              value="—"
              color="#0ea5e9" bg="#e0f2fe"
              sub="Use sidebar to view attendance, fees, performance"
            />
          </div>
        </div>

        {/* Children cards */}
        {children.length === 0 ? (
          <div className="card">
            <div className="card-body text-center py-5 text-muted">
              <i className="bi bi-emoji-frown fs-1 d-block mb-2"/>
              <h6 className="mb-2">No children linked to your account yet.</h6>
              <p className="small mb-0">
                Ask the school office to link your child's record. They'll need your <strong>email</strong>{' '}
                ({user?.email || 'your email'}) or <strong>phone number</strong> on the student record.
              </p>
            </div>
          </div>
        ) : (
          <div className="row g-3">
            {children.map((c, idx) => {
              const colors = ['#6366f1','#0ea5e9','#22c55e','#f59e0b','#8b5cf6'];
              const color = colors[idx % colors.length];
              const initials = (c.studentName || '?')
                .split(' ').filter(Boolean).slice(0, 2)
                .map(s => s[0]).join('').toUpperCase();
              const tier = c.performance?.tier;
              const tierBg = tier === 'platinum' ? '#e0f2fe' : tier === 'gold' ? '#fef3c7' : tier === 'silver' ? '#f1f5f9' : '#fed7aa';
              const tierFg = tier === 'platinum' ? '#0369a1' : tier === 'gold' ? '#b45309' : tier === 'silver' ? '#475569' : '#9a3412';

              return (
                <div key={c._id} className="col-lg-6">
                  <div className="card h-100 shadow-sm" style={{ borderRadius: 14 }}>
                    <div className="card-body">
                      {/* Child header */}
                      <div className="d-flex align-items-center gap-3 mb-3">
                        <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{ width: 56, height: 56, background: color + '25', color, fontWeight: 800, fontSize: 22 }}>
                          {initials}
                        </div>
                        <div className="flex-grow-1">
                          <h5 className="mb-0">{c.studentName}</h5>
                          <div className="small text-muted">
                            {c.classGrade}{c.section ? ` ${c.section}` : ''}
                            {c.admissionNumber && <> · #{c.admissionNumber}</>}
                          </div>
                        </div>
                        {tier && (
                          <span className="badge" style={{ background: tierBg, color: tierFg, fontWeight: 700, padding: '6px 12px' }}>
                            <i className="bi bi-trophy-fill me-1"/>{tier}
                          </span>
                        )}
                      </div>

                      {/* Attendance */}
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="small fw-semibold">
                            <i className="bi bi-calendar-check me-1 text-success"/>Attendance
                          </span>
                          <span className="small fw-bold">
                            {c.attendance?.pct == null ? '—' : `${c.attendance.pct}%`}
                            {c.attendance?.total > 0 && (
                              <span className="text-muted ms-1">({c.attendance.present}/{c.attendance.total})</span>
                            )}
                          </span>
                        </div>
                        <div className="progress" style={{ height: 8 }}>
                          <div className={`progress-bar bg-${(c.attendance?.pct || 0) >= 75 ? 'success' : (c.attendance?.pct || 0) >= 50 ? 'warning' : 'danger'}`}
                            style={{ width: `${c.attendance?.pct || 0}%` }}/>
                        </div>
                      </div>

                      {/* Performance breakdown */}
                      {c.performance ? (
                        <>
                          <div className="small fw-semibold mb-2">
                            <i className="bi bi-graph-up me-1 text-primary"/>
                            Performance — {c.performance.term || 'Latest'}
                          </div>
                          <div className="row g-2 mb-2">
                            <div className="col-6">
                              <div className="text-muted small">Academics</div>
                              <div className="fw-bold">{(c.performance.academics ?? 0).toFixed?.(1) || c.performance.academics || 0}</div>
                            </div>
                            <div className="col-6">
                              <div className="text-muted small">Sports</div>
                              <div className="fw-bold">{(c.performance.sports ?? 0).toFixed?.(1) || c.performance.sports || 0}</div>
                            </div>
                            <div className="col-6">
                              <div className="text-muted small">Behavior</div>
                              <div className="fw-bold">{(c.performance.behavior ?? 0).toFixed?.(1) || c.performance.behavior || 0}</div>
                            </div>
                            <div className="col-6">
                              <div className="text-muted small">Total</div>
                              <div className="fw-bold text-success">{(c.performance.totalScore ?? 0).toFixed?.(1) || c.performance.totalScore || 0}</div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="alert alert-light border small text-muted py-2 mb-0">
                          <i className="bi bi-info-circle me-1"/>No performance record yet for this term.
                        </div>
                      )}

                      <div className="d-flex gap-2 mt-3 flex-wrap">
                        <a href="/attendance" className="btn btn-outline-primary btn-sm flex-grow-1">
                          <i className="bi bi-calendar-check me-1"/>Attendance
                        </a>
                        <a href="/performance" className="btn btn-outline-success btn-sm flex-grow-1">
                          <i className="bi bi-graph-up me-1"/>Performance
                        </a>
                        <a href="/announcements" className="btn btn-outline-warning btn-sm flex-grow-1">
                          <i className="bi bi-megaphone me-1"/>Announcements
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

              </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  //  FACULTY DASHBOARD
  // ════════════════════════════════════════════════════════════════════════════
  if (isFaculty) {
    const fs = stats?.facultyStats || {};
    const att = fs.todayAttendance || { present: 0, absent: 0, late: 0, leave: 0, total: 0, unmarkedBatches: 0 };
    const attPct = att.total ? Math.round((att.present / att.total) * 100) : null;
    const attSlices = [
      { label: 'Present', value: att.present || 0, color: '#22c55e' },
      { label: 'Absent',  value: att.absent  || 0, color: '#ef4444' },
      { label: 'Late',    value: att.late    || 0, color: '#8b5cf6' },
      { label: 'Leave',   value: att.leave   || 0, color: '#f59e0b' },
    ].filter(s => s.value > 0);

    return (
      <div style={{ paddingBottom: 32 }}>
        {Header}

        {/* KPIs */}
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3">
            <KpiCard icon="bi-collection-fill"
              label={isEducationTenant ? 'My Classes' : 'My Batches'}
              value={fs.myBatchCount ?? 0} color="#6366f1" bg="#ede9fe"
              sub="assigned to me" />
          </div>
          <div className="col-6 col-md-3">
            <KpiCard icon="bi-people-fill" label="My Students"
              value={fs.myStudentCount ?? 0} color="#0ea5e9" bg="#e0f2fe"
              sub={isEducationTenant ? 'across my classes' : 'across my batches'} />
          </div>
          <div className="col-6 col-md-3">
            <KpiCard
              icon="bi-calendar-check-fill"
              label="Today's Attendance"
              value={attPct !== null ? `${attPct}%` : (att.unmarkedBatches > 0 ? 'Pending' : '—')}
              color={attPct >= 75 ? '#22c55e' : attPct >= 50 ? '#f59e0b' : '#ef4444'}
              bg={attPct >= 75 ? '#d1fae5' : attPct >= 50 ? '#fef3c7' : '#fee2e2'}
              sub={att.unmarkedBatches > 0 ? `${att.unmarkedBatches} batch(es) unmarked` : `${att.present}/${att.total} present`}
            />
          </div>
          <div className="col-6 col-md-3">
            <KpiCard
              icon="bi-list-task"
              label="My Tasks"
              value={fs.myPendingTasks ?? 0}
              color={(fs.myPendingTasks || 0) > 0 ? '#f59e0b' : '#22c55e'}
              bg={(fs.myPendingTasks || 0) > 0 ? '#fef3c7' : '#d1fae5'}
              sub={`of ${fs.myTotalTasks || 0} total`}
            />
          </div>
        </div>

        {/* Today's Classes + Today's Attendance Donut */}
        <div className="row g-3 mb-4">
          <div className="col-lg-7">
            <div className="card h-100 shadow-sm" style={{ borderRadius: 14 }}>
              <div className="card-header d-flex justify-content-between align-items-center" style={{ background: 'transparent', border: 'none', paddingBottom: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                  <i className="bi bi-calendar3 me-2" style={{ color: '#0ea5e9' }}></i>
                  Today's Classes
                  <span className="badge ms-2" style={{ background: '#e0f2fe', color: '#0369a1', fontSize: 10 }}>{dayName}</span>
                </div>
                <a href="/timetable" style={{ fontSize: 12, color: '#6366f1' }}>Timetable →</a>
              </div>
              <div className="card-body pt-2">
                {(fs.todayClasses || []).length === 0 ? (
                  <div className="text-center text-muted py-3">
                    <i className="bi bi-cup-hot" style={{ fontSize: 28, opacity: 0.2 }}></i>
                    <div style={{ fontSize: 13, marginTop: 8 }}>No classes scheduled for today</div>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {fs.todayClasses.map((c, i) => (
                      <div key={i} className="d-flex align-items-center gap-2 p-2 rounded" style={{ background: '#f8fafc', borderLeft: '3px solid #0ea5e9' }}>
                        <div className="flex-grow-1">
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{c.courseName}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>
                            {c.batchNumber}{c.classroom ? ` · ${c.classroom}` : ''} · {c.studentCount} students
                          </div>
                        </div>
                        <div className="text-end">
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#0ea5e9' }}>{c.startTime || '—'}</div>
                          <div style={{ fontSize: 10, color: '#94a3b8' }}>{c.endTime || ''}</div>
                        </div>
                        <a href="/attendance" className="btn btn-sm btn-outline-primary">
                          <i className="bi bi-check2-square"></i>
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-5">
            <div className="card h-100 shadow-sm" style={{ borderRadius: 14 }}>
              <div className="card-body">
                <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', marginBottom: 12 }}>
                  <i className="bi bi-pie-chart-fill me-2" style={{ color: '#22c55e' }}></i>
                  Today's Attendance
                </div>
                {attSlices.length > 0 ? (
                  <DonutChart slices={attSlices} centerLabel={attPct !== null ? `${attPct}%\nPresent` : '—'} />
                ) : (
                  <div className="text-center text-muted py-3">
                    <i className="bi bi-calendar-check" style={{ fontSize: 36, opacity: 0.2 }}></i>
                    <div style={{ fontSize: 13, marginTop: 8 }}>Not marked yet today</div>
                    <a href="/attendance" className="btn btn-primary btn-sm mt-2">
                      <i className="bi bi-pencil-square me-1"></i>Mark Attendance
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Pending Evaluations + My Tasks */}
        <div className="row g-3 mb-4">
          <div className="col-lg-6">
            <div className="card h-100 shadow-sm" style={{ borderRadius: 14 }}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                    <i className="bi bi-clipboard-check me-2" style={{ color: '#8b5cf6' }}></i>
                    Assessments
                  </div>
                  <a href="/assessments" style={{ fontSize: 12, color: '#6366f1' }}>Manage →</a>
                </div>
                <div className="row g-2">
                  <div className="col-6">
                    <div className="text-center p-3 rounded" style={{ background: '#f3e8ff' }}>
                      <div style={{ fontSize: 24, fontWeight: 800, color: '#8b5cf6' }}>{fs.assignedAssessments || 0}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>Active Assessments</div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-center p-3 rounded" style={{ background: (fs.pendingEvaluations || 0) > 0 ? '#fee2e2' : '#d1fae5' }}>
                      <div style={{ fontSize: 24, fontWeight: 800, color: (fs.pendingEvaluations || 0) > 0 ? '#ef4444' : '#22c55e' }}>{fs.pendingEvaluations || 0}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>Pending Evaluation</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card h-100 shadow-sm" style={{ borderRadius: 14 }}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                    <i className="bi bi-list-task me-2" style={{ color: '#f59e0b' }}></i>
                    My Pending Tasks
                  </div>
                  <a href="/tasks" style={{ fontSize: 12, color: '#6366f1' }}>All →</a>
                </div>
                {(fs.recentTasks || []).length === 0 ? (
                  <div className="text-center text-muted py-3">
                    <i className="bi bi-check-circle" style={{ fontSize: 28, opacity: 0.2, color: '#22c55e' }}></i>
                    <div style={{ fontSize: 13, marginTop: 8 }}>All caught up!</div>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {fs.recentTasks.map(t => {
                      const due = t.targetDate || t.dueDate;
                      const overdue = due && new Date(due) < new Date() && t.status !== 'completed';
                      const pColor = t.priority === 'urgent' ? '#ef4444' : t.priority === 'high' ? '#f59e0b' : '#0ea5e9';
                      return (
                        <div key={t._id} className="d-flex align-items-center justify-content-between p-2 rounded" style={{ background: '#f8fafc', borderLeft: `3px solid ${pColor}` }}>
                          <div className="flex-grow-1">
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{t.title}</div>
                            <div style={{ fontSize: 11, color: '#94a3b8' }}>
                              <span className="badge me-1" style={{ background: pColor + '20', color: pColor, fontSize: 9 }}>{t.priority}</span>
                              {due && <>Due {new Date(due).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</>}
                              {overdue && <span className="text-danger fw-bold ms-1">· Overdue</span>}
                            </div>
                          </div>
                          <span className="badge bg-secondary" style={{ fontSize: 9 }}>{t.status.replace('_', ' ')}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* My Batches list */}
        <div className="card shadow-sm mb-4" style={{ borderRadius: 14 }}>
          <div className="card-body">
            <div className="mb-3" style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
              <i className="bi bi-collection me-2" style={{ color: '#6366f1' }}></i>
              {isEducationTenant ? 'My Classes' : 'My Batches'}
            </div>
            {(fs.myBatches || []).length === 0 ? (
              <div className="text-center text-muted py-3">
                {isEducationTenant ? 'No classes assigned yet' : 'No batches assigned yet'}
              </div>
            ) : (
              <div className="row g-2">
                {fs.myBatches.map((b, i) => {
                  const colors = ['#6366f1','#0ea5e9','#22c55e','#f59e0b','#8b5cf6','#ef4444','#0d6efd','#14b8a6'];
                  const c = colors[i % colors.length];
                  return (
                    <div key={b._id} className="col-md-6 col-lg-3">
                      <div className="p-3 rounded" style={{ background: c + '10', border: `1px solid ${c}25` }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: c }}>{b.batchNumber}</div>
                        <div style={{ fontSize: 12, color: '#1e293b', marginTop: 2 }}>{b.courseName}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                          {b.currentStudents} students · <span className="text-capitalize">{b.status}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

              </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  //  TEAM LEAD DASHBOARD — narrow scope: my team, my batches, my approvals
  // ════════════════════════════════════════════════════════════════════════════
  if (isTeamLead) {
    const ts = stats?.trainingStats || {};
    const att = ts.todayAttendance || { present: 0, absent: 0, late: 0, leave: 0, total: 0 };
    const attPct = att.total ? Math.round((att.present / att.total) * 100) : null;

    return (
      <div style={{ paddingBottom: 32 }}>
        {Header}

        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3">
            <KpiCard icon="bi-collection-fill" label={isEducationTenant ? 'My Classes' : 'My Batches'}
              value={ts.batchCount ?? 0} color={theme.color} bg={theme.bg} sub="assigned to me" />
          </div>
          <div className="col-6 col-md-3">
            <KpiCard icon="bi-people-fill" label="My Team / Students"
              value={ts.enrollmentCount ?? 0} color="#0ea5e9" bg="#e0f2fe"
              sub="across my batches" />
          </div>
          <div className="col-6 col-md-3">
            <KpiCard icon="bi-calendar-check-fill" label="Team Attendance Today"
              value={attPct !== null ? `${attPct}%` : '—'}
              color={attPct >= 75 ? '#22c55e' : attPct >= 50 ? '#f59e0b' : '#ef4444'}
              bg={attPct >= 75 ? '#d1fae5' : attPct >= 50 ? '#fef3c7' : '#fee2e2'}
              sub={att.total ? `${att.present}/${att.total} present` : 'Not marked yet'}
            />
          </div>
          <div className="col-6 col-md-3">
            <KpiCard icon="bi-list-task" label="Tasks I Assigned"
              value={ts.pendingTasks ?? 0} color="#f59e0b" bg="#fef3c7"
              sub={`of ${ts.totalTasks || 0} total`} />
          </div>
        </div>

        {/* My Batches list — primary focus for Team Lead */}
        <div className="card shadow-sm mb-4" style={{ borderRadius: 14 }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                <i className="bi bi-collection me-2" style={{ color: theme.color }}></i>
                {isEducationTenant ? 'My Classes' : 'My Batches'}
              </div>
              <a href={isEducationTenant ? '/timetable' : '/batches'} style={{ fontSize: 12, color: theme.color }}>View all →</a>
            </div>
            {(ts.recentEnrollments || []).length === 0 ? (
              <div className="text-center text-muted py-3">
                <i className="bi bi-collection" style={{ fontSize: 28, opacity: 0.2 }}></i>
                <div style={{ fontSize: 12, marginTop: 6 }}>No batches assigned yet — ask admin to allocate.</div>
              </div>
            ) : (
              <div className="row g-2">
                {ts.recentEnrollments.slice(0, 6).map((e, i) => {
                  const colors = ['#8b5cf6','#0ea5e9','#22c55e','#f59e0b','#ef4444','#14b8a6'];
                  const c = colors[i % colors.length];
                  return (
                    <div key={e._id} className="col-md-6 col-lg-4">
                      <div className="p-3 rounded h-100" style={{ background: c + '10', border: `1px solid ${c}25` }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: c }}>
                          {e.batch?.batchNumber || '—'}
                        </div>
                        <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 600, marginTop: 2 }}>
                          {e.course?.courseName || '—'}
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                          {e.student?.firstName} {e.student?.lastName} · {timeAgo(e.createdAt)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Approvals queue — Course Records for team_lead */}
        <div className="row g-3 mb-4">
          <div className="col-lg-6">
            <div className="card h-100 shadow-sm" style={{ borderRadius: 14 }}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                    <i className="bi bi-check2-square me-2" style={{ color: theme.color }}></i>
                    Pending Approvals
                  </div>
                  <a href="/course-records" style={{ fontSize: 12, color: theme.color }}>Review →</a>
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                  Course records & tasks awaiting your sign-off.
                </div>
                <div className="row g-2">
                  <div className="col-6">
                    <div className="text-center p-3 rounded" style={{ background: '#f3e8ff' }}>
                      <div style={{ fontSize: 24, fontWeight: 800, color: '#8b5cf6' }}>{ts.pendingTasks || 0}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>Pending Tasks</div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-center p-3 rounded" style={{ background: '#fef3c7' }}>
                      <div style={{ fontSize: 24, fontWeight: 800, color: '#f59e0b' }}>{ts.pendingLeaves || 0}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>Leave Requests</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card h-100 shadow-sm" style={{ borderRadius: 14 }}>
              <div className="card-body">
                <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', marginBottom: 14 }}>
                  <i className="bi bi-lightning-fill me-2" style={{ color: theme.color }}></i>
                  Quick Actions
                </div>
                <div className="row g-2">
                  {[
                    { label: 'Mark Attendance', icon: 'bi-calendar-check', href: '/attendance' },
                    { label: 'Course Records',  icon: 'bi-journal-check',  href: '/course-records' },
                    { label: 'Tasks',           icon: 'bi-check2-square',  href: '/tasks' },
                    { label: 'Daily Reports',   icon: 'bi-clipboard-data', href: '/daily-reports' },
                  ].map(q => (
                    <div className="col-6" key={q.label}>
                      <a href={q.href} className="text-decoration-none">
                        <div className="d-flex align-items-center gap-2 p-2 rounded"
                          style={{ background: theme.bg, border: `1px solid ${theme.color}25` }}>
                          <i className={`bi ${q.icon}`} style={{ fontSize: 16, color: theme.color }}></i>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>{q.label}</span>
                        </div>
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

              </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  //  STAFF DASHBOARD — tasks / leave / work-attendance focus only
  // ════════════════════════════════════════════════════════════════════════════
  if (isStaff) {
    const ts = stats?.trainingStats || {};
    return (
      <div style={{ paddingBottom: 32 }}>
        {Header}
        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <KpiCard icon="bi-list-task" label="My Pending Tasks"
              value={ts.pendingTasks ?? 0} color={theme.color} bg={theme.bg}
              sub={`of ${ts.totalTasks || 0} assigned`} />
          </div>
          <div className="col-md-4">
            <KpiCard icon="bi-calendar-x-fill" label="Leave Requests"
              value={ts.pendingLeaves ?? 0} color="#0ea5e9" bg="#e0f2fe"
              sub="awaiting decision" />
          </div>
          <div className="col-md-4">
            <KpiCard icon="bi-clock-fill" label="Today"
              value={dayName.slice(0, 3)} color="#22c55e" bg="#d1fae5"
              sub={dateStr} />
          </div>
        </div>
        <div className="row g-3 mb-4">
          <div className="col-lg-12">
            <div className="card shadow-sm" style={{ borderRadius: 14 }}>
              <div className="card-body">
                <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', marginBottom: 14 }}>
                  <i className="bi bi-lightning-fill me-2" style={{ color: theme.color }}></i>
                  What You Can Do
                </div>
                <div className="row g-2">
                  {[
                    { label: 'My Tasks',         icon: 'bi-check2-square',  href: '/tasks' },
                    { label: 'Apply Leave',      icon: 'bi-calendar-plus',  href: '/leave' },
                    { label: 'Work Attendance',  icon: 'bi-clock-history',  href: '/work-attendance' },
                    { label: 'Feedback',         icon: 'bi-chat-quote',     href: '/feedback' },
                  ].map(q => (
                    <div className="col-6 col-md-3" key={q.label}>
                      <a href={q.href} className="text-decoration-none">
                        <div className="d-flex flex-column align-items-center justify-content-center gap-2 p-3 rounded text-center"
                          style={{ background: theme.bg, border: `1px solid ${theme.color}25`, minHeight: 80 }}>
                          <i className={`bi ${q.icon}`} style={{ fontSize: 22, color: theme.color }}></i>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>{q.label}</span>
                        </div>
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
              </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  //  VIEWER DASHBOARD — minimal welcome + read-only links
  // ════════════════════════════════════════════════════════════════════════════
  if (isViewer) {
    return (
      <div style={{ paddingBottom: 32 }}>
        {Header}
        <div className="card shadow-sm" style={{ borderRadius: 14 }}>
          <div className="card-body text-center py-5">
            <i className="bi bi-eye" style={{ fontSize: 48, color: theme.color, opacity: 0.6 }}></i>
            <h5 className="mt-3 fw-bold">Read-only access</h5>
            <p className="text-muted" style={{ fontSize: 13 }}>
              Your role allows viewing only. Use the sidebar to browse modules your administrator has shared with you.
            </p>
          </div>
        </div>
              </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  //  ADMIN / MANAGER DASHBOARD
  // ════════════════════════════════════════════════════════════════════════════
  const ts = stats?.trainingStats || {};
  const att = ts.todayAttendance || { present: 0, absent: 0, late: 0, leave: 0, total: 0 };
  const attPct = att.total ? Math.round((att.present / att.total) * 100) : null;
  const attSlices = [
    { label: 'Present', value: att.present || 0, color: '#22c55e' },
    { label: 'Absent',  value: att.absent  || 0, color: '#ef4444' },
    { label: 'Leave',   value: att.leave   || 0, color: '#f59e0b' },
    { label: 'Late',    value: att.late    || 0, color: '#8b5cf6' },
  ].filter(s => s.value > 0);

  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const trendData = (ts.enrollmentTrend || []).map(e => {
    const [, m] = (e._id || '').split('-');
    return { label: months[parseInt(m, 10) - 1] || e._id, value: e.count };
  });

  const modCounts = (stats?.moduleCounts || []).slice(0, 6);
  const maxCount  = Math.max(...modCounts.map(m => m.count), 1);
  const MOD_COLORS = ['#6366f1','#0ea5e9','#22c55e','#f59e0b','#ef4444','#8b5cf6'];

  // Scope label
  const scopeLabel = isTeamLead
    ? `Team View — your ${isEducationTenant ? 'classes' : 'batches'} only`
    : isManager ? 'Org View — operational' : 'Org View — full';

  return (
    <div style={{ paddingBottom: 32 }}>
      {Header}

      <div className="alert alert-light border d-flex align-items-center gap-2 py-2 mb-3" style={{ fontSize: 12 }}>
        <i className="bi bi-info-circle text-primary"></i>
        <span>
          <strong>{scopeLabel}</strong>
          {isManager && ' (revenue hidden)'}
          {isTeamLead && ` (only your assigned ${isEducationTenant ? 'classes' : 'batches'})`}
        </span>
      </div>

      {/* KPIs — vary slightly by role AND by industry (education vs training).
          Education tenants see "Classes / Grades / Students" terminology;
          training tenants see "Courses / Batches / Enrollments". */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-4 col-lg-2">
          <KpiCard icon="bi-journal-bookmark-fill"
            label={isEducationTenant ? 'Subjects' : 'Courses'}
            value={ts.courseCount ?? '—'}
            color="#6366f1" bg="#ede9fe"
            sub={isEducationTenant ? 'configured' : 'active courses'} />
        </div>
        <div className="col-6 col-md-4 col-lg-2">
          <KpiCard icon="bi-collection-fill"
            label={isEducationTenant
              ? (isTeamLead ? 'My Classes' : 'Classes / Grades')
              : (isTeamLead ? 'My Batches' : 'Batches')}
            value={ts.batchCount ?? '—'}
            color="#0ea5e9" bg="#e0f2fe"
            sub={isEducationTenant ? 'active class sections' : 'running batches'} />
        </div>
        <div className="col-6 col-md-4 col-lg-2">
          <KpiCard icon="bi-person-plus-fill"
            label={isEducationTenant ? 'Students' : 'Enrollments'}
            value={ts.enrollmentCount ?? '—'}
            color="#22c55e" bg="#d1fae5"
            sub={isEducationTenant ? 'enrolled' : 'active students'} />
        </div>
        <div className="col-6 col-md-4 col-lg-2">
          <KpiCard
            icon="bi-calendar-check-fill"
            label="Today's Attendance"
            value={attPct !== null ? `${attPct}%` : '—'}
            color={attPct >= 75 ? '#22c55e' : attPct >= 50 ? '#f59e0b' : '#ef4444'}
            bg={attPct >= 75 ? '#d1fae5' : attPct >= 50 ? '#fef3c7' : '#fee2e2'}
            sub={att.total ? `${att.present}/${att.total} present` : 'Not marked yet'}
          />
        </div>
        {isAdminLike ? (
          <div className="col-6 col-md-4 col-lg-2">
            <KpiCard
              icon="bi-currency-rupee" label="Revenue"
              value={ts.totalRevenue ? `₹${(ts.totalRevenue / 1000).toFixed(0)}K` : '—'}
              color="#8b5cf6" bg="#f3e8ff"
              sub={ts.pendingFees ? `₹${(ts.pendingFees / 1000).toFixed(0)}K pending` : 'collected'}
            />
          </div>
        ) : (
          <div className="col-6 col-md-4 col-lg-2">
            <KpiCard
              icon="bi-calendar-x-fill" label="Pending Leaves"
              value={ts.pendingLeaves ?? 0}
              color={(ts.pendingLeaves || 0) > 0 ? '#ef4444' : '#22c55e'}
              bg={(ts.pendingLeaves || 0) > 0 ? '#fee2e2' : '#d1fae5'}
              sub="awaiting review"
            />
          </div>
        )}
        <div className="col-6 col-md-4 col-lg-2">
          <KpiCard
            icon="bi-list-task" label="Pending Tasks" value={ts.pendingTasks ?? '—'}
            color="#f59e0b" bg="#fef3c7"
            sub={`${ts.facultyCount || 0} faculty · ${ts.studentCount || 0} students`}
          />
        </div>
      </div>

      {!isEducationTenant && isAdminOrAbove && ts.collegeStudentCount !== undefined && (
        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
              <i className="bi bi-mortarboard-fill me-2 text-primary"></i>Student Registrations
            </div>
            <a href="/users" className="text-decoration-none" style={{ fontSize: 12 }}>View all</a>
          </div>
          <div className="row g-3">
            <div className="col-6 col-lg-4">
              <KpiCard
                icon="bi-person-fill" label="Individual Students" value={ts.individualStudentCount ?? 0}
                color="#0ea5e9" bg="#e0f2fe" sub="Direct and paid-course registrations"
                onClick={() => window.location.assign('/users?tab=students')}
              />
            </div>
            <div className="col-6 col-lg-4">
              <KpiCard
                icon="bi-buildings-fill" label="College Students" value={ts.collegeStudentCount ?? 0}
                color="#6366f1" bg="#ede9fe" sub="Registered through college portals"
                onClick={() => window.location.assign('/users?tab=college-students')}
              />
            </div>
            <div className="col-12 col-lg-4">
              <KpiCard
                icon="bi-hourglass-split" label="Pending College Authorization" value={ts.pendingCollegeCount ?? 0}
                color="#f59e0b" bg="#fef3c7" sub="Awaiting verification"
                onClick={() => window.location.assign('/lms?tab=college-review')}
              />
            </div>
          </div>
        </div>
      )}

      {/* Trend + Donut */}
      <div className="row g-3 mb-4">
        <div className="col-lg-7">
          <div className="card h-100 shadow-sm" style={{ borderRadius: 14 }}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                    <i className="bi bi-graph-up me-2 text-primary"></i>Enrollment Trend
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>New enrollments — last 6 months</div>
                </div>
                <span className="badge" style={{ background: '#ede9fe', color: '#6366f1', fontSize: 11 }}>Monthly</span>
              </div>
              <BarChart data={trendData} color="#6366f1" />
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="card h-100 shadow-sm" style={{ borderRadius: 14 }}>
            <div className="card-body">
              <div className="mb-3">
                <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                  <i className="bi bi-pie-chart-fill me-2 text-success"></i>Today's Attendance
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>{dateStr}</div>
              </div>
              {attSlices.length > 0 ? (
                <DonutChart slices={attSlices} centerLabel={attPct !== null ? `${attPct}%\nPresent` : '—'} />
              ) : (
                <div className="text-center py-3 text-muted">
                  <i className="bi bi-calendar-check" style={{ fontSize: 36, opacity: 0.2 }}></i>
                  <div style={{ fontSize: 13, marginTop: 8 }}>Attendance not marked today</div>
                  <a href="/attendance" className="btn btn-primary btn-sm mt-2">
                    <i className="bi bi-pencil-square me-1"></i>Mark Now
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Records by Module + Attendance Summary */}
      <div className="row g-3 mb-4">
        <div className="col-lg-5">
          <div className="card h-100 shadow-sm" style={{ borderRadius: 14 }}>
            <div className="card-body">
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', marginBottom: 16 }}>
                <i className="bi bi-bar-chart-fill me-2" style={{ color: '#6366f1' }}></i>
                Records by Module
              </div>
              {modCounts.length === 0 ? (
                <EmptyChart label="modules" />
              ) : (
                modCounts.map((m, i) => (
                  <HorizontalBar
                    key={m._id}
                    label={m._id?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '—'}
                    value={m.count}
                    max={maxCount}
                    color={MOD_COLORS[i % MOD_COLORS.length]}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="card h-100 shadow-sm" style={{ borderRadius: 14 }}>
            <div className="card-header d-flex justify-content-between align-items-center" style={{ background: 'transparent', border: 'none', paddingBottom: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                <i className="bi bi-calendar-check me-2" style={{ color: '#22c55e' }}></i>
                Today's Attendance Summary
              </div>
              <a href="/attendance" style={{ fontSize: 12, color: '#6366f1' }}>Mark →</a>
            </div>
            <div className="card-body pt-2">
              {att.total > 0 ? (
                <div className="row g-3">
                  {[
                    { label: 'Present', value: att.present, color: '#22c55e', icon: 'bi-check-circle-fill' },
                    { label: 'Absent', value: att.absent, color: '#ef4444', icon: 'bi-x-circle-fill' },
                    { label: 'Late', value: att.late, color: '#8b5cf6', icon: 'bi-clock-fill' },
                    { label: 'Leave', value: att.leave, color: '#f59e0b', icon: 'bi-calendar-x-fill' },
                  ].map(s => (
                    <div key={s.label} className="col-6 col-md-3">
                      <div className="text-center p-3 rounded" style={{ background: s.color + '10' }}>
                        <i className={`bi ${s.icon} d-block mb-1`} style={{ fontSize: 22, color: s.color }}></i>
                        <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{s.label}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted py-3">
                  <i className="bi bi-calendar-check" style={{ fontSize: 36, opacity: 0.2 }}></i>
                  <div style={{ fontSize: 13, marginTop: 8 }}>Attendance not marked today</div>
                  <a href="/attendance" className="btn btn-primary btn-sm mt-2"><i className="bi bi-pencil-square me-1"></i>Mark Now</a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Enrollments + Quick Actions */}
      <div className="row g-3 mb-4">
        <div className="col-lg-6">
          <div className="card h-100 shadow-sm" style={{ borderRadius: 14 }}>
            <div className="card-header d-flex justify-content-between align-items-center" style={{ background: 'transparent', border: 'none', paddingBottom: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                <i className="bi bi-person-plus me-2" style={{ color: '#22c55e' }}></i>
                {isEducationTenant ? 'Recent Admissions' : 'Recent Enrollments'}
              </div>
              {/* Students don't have access to the management
                  enrollments / admissions pages — they go to the
                  student course view instead. */}
              <a href={isStudent ? '/student-course' : (isEducationTenant ? '/admissions' : '/enrollments')} style={{ fontSize: 12, color: '#6366f1' }}>All →</a>
            </div>
            <div className="card-body pt-2">
              {(ts.recentEnrollments || []).length === 0 ? (
                <div className="text-center text-muted py-3">
                  <i className="bi bi-person-plus" style={{ fontSize: 28, opacity: 0.2 }}></i>
                  <div style={{ fontSize: 12, marginTop: 6 }}>{isEducationTenant ? 'No admissions yet' : 'No enrollments yet'}</div>
                </div>
              ) : (
                <div className="d-flex flex-column gap-1">
                  {ts.recentEnrollments.map((e, i) => {
                    const colors = ['#6366f1','#0ea5e9','#22c55e','#f59e0b','#8b5cf6'];
                    const c = colors[i % colors.length];
                    const initials = `${e.student?.firstName?.[0] || ''}${e.student?.lastName?.[0] || ''}`.toUpperCase() || '?';
                    return (
                      <div key={e._id} className="d-flex align-items-center gap-2 py-1">
                        <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{ width: 32, height: 32, background: c + '20', color: c, fontWeight: 700, fontSize: 12 }}>
                          {initials}
                        </div>
                        <div className="flex-grow-1">
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                            {e.student?.firstName} {e.student?.lastName}
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>
                            {e.course?.courseName || '—'} · {e.batch?.batchNumber || '—'}
                          </div>
                        </div>
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>{timeAgo(e.createdAt)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="card h-100 shadow-sm" style={{ borderRadius: 14 }}>
            <div className="card-body">
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', marginBottom: 14 }}>
                <i className="bi bi-lightning-fill me-2" style={{ color: '#f59e0b' }}></i>
                Quick Actions
              </div>
              <div className="row g-2">
                {(isStudent ? [
                  // Student-only Quick Actions — only links to
                  // student-friendly modules the student role
                  // actually has in their activeModules.
                  { label: 'My Course',      icon: 'bi-mortarboard',      href: '/student-course',     key: 'student_course', color: '#6366f1' },
                  { label: 'Attendance',     icon: 'bi-calendar-check',   href: '/attendance',         key: 'attendance',     color: '#f59e0b' },
                  { label: 'Assessments',    icon: 'bi-clipboard-check',  href: '/assessments',        key: 'assessments',    color: '#8b5cf6' },
                  { label: 'Tasks',          icon: 'bi-check2-square',    href: '/tasks',              key: 'tasks',          color: '#0d6efd' },
                ] : isEducationTenant ? [
                  { label: 'Students',       icon: 'bi-person-badge',     href: '/students',           key: 'students',     color: '#6366f1' },
                  { label: 'Admissions',     icon: 'bi-person-plus',      href: '/admissions',         key: 'admissions',   color: '#22c55e' },
                  { label: 'Attendance',     icon: 'bi-calendar-check',   href: '/attendance',         key: 'attendance',   color: '#f59e0b' },
                  { label: 'Exams',          icon: 'bi-journal-check',    href: '/exams',              key: 'exams',        color: '#8b5cf6' },
                  { label: 'Fees',           icon: 'bi-cash-coin',        href: '/fees',               key: 'fees',         color: '#ef4444' },
                  { label: 'Timetable',      icon: 'bi-calendar3-week',   href: '/timetable',          key: 'timetable',    color: '#0ea5e9' },
                  { label: 'Course Content', icon: 'bi-play-circle',      href: '/course-content-lms', key: 'advanced_course_content', color: '#0d6efd' },
                  { label: 'Syllabus',       icon: 'bi-journal-bookmark', href: '/syllabus',           key: 'syllabus',     color: '#14b8a6' },
                ] : [
                  { label: 'Courses',        icon: 'bi-journal-bookmark', href: '/courses',       key: 'courses',        color: '#6366f1' },
                  { label: 'Batches',        icon: 'bi-collection',       href: '/batches',       key: 'batches',        color: '#0ea5e9' },
                  { label: 'Enrollments',    icon: 'bi-person-plus',      href: '/enrollments',   key: 'enrollments',    color: '#22c55e' },
                  { label: 'Attendance',     icon: 'bi-calendar-check',   href: '/attendance',    key: 'attendance',     color: '#f59e0b' },
                  { label: 'Assessments',    icon: 'bi-clipboard-check',  href: '/assessments',   key: 'assessments',    color: '#8b5cf6' },
                  { label: 'Course Content', icon: 'bi-play-circle',      href: '/course-content',key: 'course-content', color: '#ef4444' },
                  { label: 'Tasks',          icon: 'bi-check2-square',    href: '/tasks',         key: 'tasks',          color: '#0d6efd' },
                  { label: 'HRM',            icon: 'bi-people',           href: '/hrm',           key: 'hrm',            color: '#14b8a6' },
                // Filter by role's menuAccess so users who reach this section
                // only see links they're authorised to navigate to.
                // Admin / manager roles have null menuAccess (unrestricted).
                ]).filter(q => !roleDoc?.menuAccess?.length || roleDoc.menuAccess.includes(q.key))
                .map(q => (
                  <div key={q.label} className="col-6 col-md-3">
                    <a href={q.href} className="text-decoration-none">
                      <div className="d-flex align-items-center gap-2 p-2 rounded"
                        style={{ background: q.color + '10', border: `1px solid ${q.color}20`, cursor: 'pointer' }}>
                        <i className={`bi ${q.icon}`} style={{ fontSize: 16, color: q.color }}></i>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>{q.label}</span>
                      </div>
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

          </div>
  );
}
